import {
  applyMotionPreference,
  applyPointerCursorPreference,
  isMotionPreference,
  resolveMotionPreference,
  type MotionPreference,
} from "@salestrak/ui";
import React from "react";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";

/**
 * The motion choices with display labels — render these as the choices
 * anywhere the preference is user-configurable (the preferences screen's
 * toggle group). This file is their canonical home: it owns the effect that
 * honors them.
 */
export const REDUCE_MOTION_OPTIONS: ReadonlyArray<{
  label: string;
  value: MotionPreference;
}> = [
  { label: "System", value: "system" },
  { label: "On", value: "on" },
  { label: "Off", value: "off" },
];

interface InteractionPreferences {
  reduceMotion: MotionPreference;
  /** null = never set, which is ON — the shipped default. */
  pointerCursors: boolean;
}

const DEFAULTS: InteractionPreferences = {
  reduceMotion: "system",
  pointerCursors: true,
};

/**
 * Preferences arrive over Apollo, so the first paint lands before they do.
 * For most settings that gap is invisible, but reduced motion is an
 * accessibility preference — a flash of animation on every reload is exactly
 * what the person asking for it cannot have. The mirror seeds the document
 * synchronously from localStorage, the same trick ThemeAppearanceProvider
 * uses; the server stays the source of truth and overwrites it on load.
 */
const STORAGE_KEY = "interaction_preferences";

function readMirror(): InteractionPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULTS;
    const { reduceMotion, pointerCursors } = parsed as Record<string, unknown>;
    return {
      reduceMotion:
        typeof reduceMotion === "string" && isMotionPreference(reduceMotion)
          ? reduceMotion
          : "system",
      pointerCursors: pointerCursors !== false,
    };
  } catch {
    // A hand-edited entry or a browser refusing storage resolves to the
    // shipped defaults rather than throwing during the first render.
    return DEFAULTS;
  }
}

function writeMirror(value: InteractionPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage disabled or full. The preference still round-trips through the
    // API; the only thing lost is the pre-paint seed on the next reload.
  }
}

/**
 * Applies the reduced-motion and pointer-cursor preferences to `<html>`.
 * Must render inside AppProviders (needs Apollo); seeds from the mirror while
 * logged out / loading. Writes are just `updatePreference` calls from the
 * preferences screen — the optimistic cache update re-renders this effect, so
 * no local commit machinery is needed.
 */
export function AppInteractionPreferencesProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { preferences } = useUserSettings();
  const [mirror] = React.useState(readMirror);

  const applied = React.useMemo<InteractionPreferences>(() => {
    if (!preferences) return mirror;
    return {
      reduceMotion: resolveMotionPreference(preferences.reduceMotion),
      pointerCursors: preferences.pointerCursors ?? true,
    };
  }, [preferences, mirror]);

  // Layout, not passive: the attributes gate what the stylesheet animates, so
  // they have to land before the browser paints the render that produced them.
  React.useLayoutEffect(() => {
    applyMotionPreference(applied.reduceMotion);
    applyPointerCursorPreference(applied.pointerCursors);
    writeMirror(applied);
  }, [applied]);

  return <>{children}</>;
}
